/**
 * Gestione abbonamenti e limiti di utilizzo per aziende - Solo Superuser
 */

import React, { useState, useEffect, useCallback } from 'react';
import { api } from '../services/api';

const PLAN_DEFAULTS = {
  PRO:        { word_limit: 250000,   query_limit: 1000 },
  BUSINESS:   { word_limit: 500000,   query_limit: 1500 },
  ENTERPRISE: { word_limit: 5000000,  query_limit: 3000 },
};

const PLAN_COLORS = {
  PRO:        'bg-blue-100 text-blue-800',
  BUSINESS:   'bg-purple-100 text-purple-800',
  ENTERPRISE: 'bg-yellow-100 text-yellow-800',
};

function formatNumber(n) {
  return n?.toLocaleString('it-IT') ?? '—';
}

function UsageBar({ used, limit, label }) {
  const pct = limit > 0 ? Math.min(100, Math.round((used / limit) * 100)) : 0;
  const color =
    pct >= 90 ? 'bg-red-500' :
    pct >= 70 ? 'bg-yellow-400' :
    'bg-blue-500';

  return (
    <div className="mb-1">
      <div className="flex justify-between text-xs text-gray-600 mb-0.5">
        <span>{label}</span>
        <span>{formatNumber(used)} / {formatNumber(limit)} ({pct}%)</span>
      </div>
      <div className="w-full bg-gray-200 rounded-full h-1.5">
        <div
          className={`${color} h-1.5 rounded-full transition-all`}
          style={{ width: `${pct}%` }}
        />
      </div>
    </div>
  );
}

const SubscriptionManagement = () => {
  const [companies, setCompanies]     = useState([]);
  const [usages, setUsages]           = useState({});       // { companyId: usageObj }
  const [loading, setLoading]         = useState(true);
  const [error, setError]             = useState('');
  const [success, setSuccess]         = useState('');

  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages]   = useState(1);
  const [searchTerm, setSearchTerm]   = useState('');
  const [filterPlan, setFilterPlan]   = useState('all');

  // Modal modifica limiti
  const [showModal, setShowModal]           = useState(false);
  const [selectedCompany, setSelectedCompany] = useState(null);
  const [editForm, setEditForm]             = useState({
    subscription_plan: 'PRO',
    custom_word_limit: '',
    custom_query_limit: '',
  });
  const [saving, setSaving]       = useState(false);
  const [resetting, setResetting] = useState(null); // company_id in corso di reset

  // Carica aziende
  const loadCompanies = useCallback(async () => {
    try {
      setLoading(true);
      setError('');
      const data = await api.listCompanies(currentPage, 20, searchTerm);
      let list = data.companies || [];
      if (filterPlan !== 'all') {
        list = list.filter(c => c.subscription_plan === filterPlan);
      }
      setCompanies(list);
      setTotalPages(data.pages);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [currentPage, searchTerm, filterPlan]);

  useEffect(() => {
    const t = setTimeout(loadCompanies, 100);
    return () => clearTimeout(t);
  }, [loadCompanies]);

  // Carica utilizzo per le aziende visibili
  useEffect(() => {
    if (companies.length === 0) return;
    companies.forEach(company => {
      if (usages[company.id] !== undefined) return; // già caricato
      setUsages(prev => ({ ...prev, [company.id]: null })); // null = loading
      api.getCompanyUsage(company.id)
        .then(data => setUsages(prev => ({ ...prev, [company.id]: data })))
        .catch(() => setUsages(prev => ({ ...prev, [company.id]: false }))); // false = errore
    });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [companies]);

  const openEditModal = (company) => {
    setSelectedCompany(company);
    setEditForm({
      subscription_plan:  company.subscription_plan || 'PRO',
      custom_word_limit:  company.custom_word_limit  ? String(company.custom_word_limit)  : '',
      custom_query_limit: company.custom_query_limit ? String(company.custom_query_limit) : '',
    });
    setError('');
    setShowModal(true);
  };

  const closeModal = () => {
    setShowModal(false);
    setSelectedCompany(null);
  };

  const handleSave = async (e) => {
    e.preventDefault();
    if (!selectedCompany) return;
    setSaving(true);
    setError('');
    try {
      await api.updateCompany(selectedCompany.id, {
        subscription_plan:  editForm.subscription_plan,
        custom_word_limit:  editForm.custom_word_limit  ? parseInt(editForm.custom_word_limit,  10) : null,
        custom_query_limit: editForm.custom_query_limit ? parseInt(editForm.custom_query_limit, 10) : null,
      });
      setSuccess(`Limiti di "${selectedCompany.ragione_sociale}" aggiornati con successo!`);
      closeModal();
      // Invalida il cache utilizzo per questa azienda e ricarica
      setUsages(prev => { const n = { ...prev }; delete n[selectedCompany.id]; return n; });
      loadCompanies();
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      setError(err.message);
    } finally {
      setSaving(false);
    }
  };

  const handleResetUsage = async (company) => {
    if (!window.confirm(
      `Azzerare i contatori mensili di "${company.ragione_sociale}"?\n\n` +
      `Parole e query torneranno a 0 e period_start verrà impostato al 1° del mese corrente.`
    )) return;

    setResetting(company.id);
    setError('');
    try {
      await api.resetCompanyUsage(company.id);
      setSuccess(`Contatori di "${company.ragione_sociale}" azzerati con successo.`);
      setUsages(prev => { const n = { ...prev }; delete n[company.id]; return n; });
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      setError(`Reset fallito: ${err.message}`);
    } finally {
      setResetting(null);
    }
  };

  // Conteggi per piano (dai dati caricati)
  const planCounts = companies.reduce((acc, c) => {
    acc[c.subscription_plan] = (acc[c.subscription_plan] || 0) + 1;
    return acc;
  }, {});

  return (
    <div className="max-w-7xl mx-auto px-4 py-8">
      <h1 className="text-3xl font-bold mb-2">Gestione Abbonamenti</h1>
      <p className="text-gray-500 mb-8 text-sm">
        Configura il piano e i limiti di utilizzo per ogni azienda. I contatori si azzerano automaticamente ogni mese e possono essere resettati manualmente.
      </p>

      {/* Messaggi */}
      {error && (
        <div className="mb-4 bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded">
          {error}
        </div>
      )}
      {success && (
        <div className="mb-4 bg-green-100 border border-green-400 text-green-700 px-4 py-3 rounded">
          {success}
        </div>
      )}

      {/* Cards piani */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-8">
        {['PRO', 'BUSINESS', 'ENTERPRISE'].map(plan => (
          <div key={plan} className="bg-white p-6 rounded-lg shadow">
            <div className="flex items-center justify-between mb-2">
              <span className={`px-2 py-1 text-xs font-bold rounded ${PLAN_COLORS[plan]}`}>{plan}</span>
              <span className="text-3xl font-bold text-gray-800">{planCounts[plan] || 0}</span>
            </div>
            <div className="text-gray-500 text-xs mt-1">
              Default: {formatNumber(PLAN_DEFAULTS[plan].word_limit)} parole / {formatNumber(PLAN_DEFAULTS[plan].query_limit)} query
            </div>
          </div>
        ))}
      </div>

      {/* Barra ricerca e filtri */}
      <div className="bg-white p-4 rounded-lg shadow mb-6">
        <div className="flex flex-col md:flex-row gap-4">
          <div className="flex-1">
            <input
              type="text"
              placeholder="Cerca per ragione sociale..."
              value={searchTerm}
              onChange={e => { setSearchTerm(e.target.value); setCurrentPage(1); }}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>
          <select
            value={filterPlan}
            onChange={e => { setFilterPlan(e.target.value); setCurrentPage(1); }}
            className="px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
          >
            <option value="all">Tutti i piani</option>
            <option value="PRO">PRO</option>
            <option value="BUSINESS">BUSINESS</option>
            <option value="ENTERPRISE">ENTERPRISE</option>
          </select>
        </div>
      </div>

      {/* Tabella */}
      <div className="bg-white rounded-lg shadow overflow-hidden">
        {loading ? (
          <div className="p-8 text-center text-gray-500">Caricamento...</div>
        ) : companies.length === 0 ? (
          <div className="p-8 text-center text-gray-500">Nessuna azienda trovata</div>
        ) : (
          <>
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Azienda</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Piano</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-72">Utilizzo Parole</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-72">Utilizzo Query</th>
                  <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">Azioni</th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {companies.map(company => {
                  const usage = usages[company.id];
                  return (
                    <tr key={company.id} className="hover:bg-gray-50">
                      <td className="px-6 py-4">
                        <div className="flex items-center gap-3">
                          {company.logo ? (
                            <img src={company.logo} alt={company.ragione_sociale} className="h-8 w-8 object-contain rounded" />
                          ) : (
                            <div className="h-8 w-8 bg-gray-200 rounded flex items-center justify-center text-gray-400 text-xs">Az</div>
                          )}
                          <div>
                            <div className="text-sm font-medium text-gray-900">{company.ragione_sociale}</div>
                            <div className="text-xs text-gray-500">{company.users_count} utenti</div>
                          </div>
                        </div>
                      </td>
                      <td className="px-6 py-4">
                        <span className={`px-2 py-1 text-xs font-bold rounded ${PLAN_COLORS[company.subscription_plan] || 'bg-gray-100 text-gray-700'}`}>
                          {company.subscription_plan}
                        </span>
                        {(company.custom_word_limit || company.custom_query_limit) && (
                          <div className="text-xs text-gray-400 mt-1">Limiti personalizzati</div>
                        )}
                      </td>
                      <td className="px-6 py-4 w-72">
                        {usage === null ? (
                          <div className="text-xs text-gray-400">Caricamento...</div>
                        ) : usage === false ? (
                          <div className="text-xs text-red-400">Errore caricamento</div>
                        ) : usage ? (
                          <>
                            <UsageBar used={usage.words_used} limit={usage.word_limit} label="Parole" />
                            {usage.period_start && (
                              <div className="text-xs text-gray-400 mt-1">
                                Periodo dal {new Date(usage.period_start).toLocaleDateString('it-IT')}
                              </div>
                            )}
                          </>
                        ) : (
                          <UsageBar used={0} limit={company.effective_word_limit} label="Parole" />
                        )}
                      </td>
                      <td className="px-6 py-4 w-72">
                        {usage === null ? (
                          <div className="text-xs text-gray-400">Caricamento...</div>
                        ) : usage === false ? (
                          <div className="text-xs text-red-400">Errore caricamento</div>
                        ) : usage ? (
                          <UsageBar used={usage.queries_used} limit={usage.query_limit} label="Query" />
                        ) : (
                          <UsageBar used={0} limit={company.effective_query_limit} label="Query" />
                        )}
                      </td>
                      <td className="px-6 py-4 text-right">
                        <div className="flex items-center justify-end gap-3">
                          <button
                            onClick={() => openEditModal(company)}
                            className="text-blue-600 hover:text-blue-900 text-sm font-medium"
                          >
                            Modifica Limiti
                          </button>
                          <button
                            onClick={() => handleResetUsage(company)}
                            disabled={resetting === company.id}
                            className="text-orange-600 hover:text-orange-900 text-sm font-medium disabled:opacity-50"
                            title="Azzera i contatori mensili di questa azienda"
                          >
                            {resetting === company.id ? 'Reset...' : 'Reset Mensile'}
                          </button>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>

            {/* Paginazione */}
            {totalPages > 1 && (
              <div className="bg-white px-4 py-3 flex items-center justify-between border-t border-gray-200 sm:px-6">
                <div className="hidden sm:flex-1 sm:flex sm:items-center sm:justify-between">
                  <p className="text-sm text-gray-700">
                    Pagina <span className="font-medium">{currentPage}</span> di{' '}
                    <span className="font-medium">{totalPages}</span>
                  </p>
                  <nav className="relative z-0 inline-flex rounded-md shadow-sm -space-x-px">
                    <button
                      onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
                      disabled={currentPage === 1}
                      className="relative inline-flex items-center px-2 py-2 rounded-l-md border border-gray-300 bg-white text-sm text-gray-500 hover:bg-gray-50 disabled:opacity-50"
                    >←</button>
                    {[...Array(Math.min(totalPages, 5))].map((_, i) => {
                      const page = i + 1;
                      return (
                        <button
                          key={page}
                          onClick={() => setCurrentPage(page)}
                          className={`relative inline-flex items-center px-4 py-2 border text-sm font-medium ${
                            currentPage === page
                              ? 'z-10 bg-blue-50 border-blue-500 text-blue-600'
                              : 'bg-white border-gray-300 text-gray-500 hover:bg-gray-50'
                          }`}
                        >{page}</button>
                      );
                    })}
                    <button
                      onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
                      disabled={currentPage === totalPages}
                      className="relative inline-flex items-center px-2 py-2 rounded-r-md border border-gray-300 bg-white text-sm text-gray-500 hover:bg-gray-50 disabled:opacity-50"
                    >→</button>
                  </nav>
                </div>
              </div>
            )}
          </>
        )}
      </div>

      {/* Modal Modifica Limiti */}
      {showModal && selectedCompany && (
        <div className="fixed z-10 inset-0 overflow-y-auto">
          <div className="flex items-center justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
            <div className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" onClick={closeModal} />
            <div className="inline-block align-bottom bg-white rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-lg sm:w-full">
              <form onSubmit={handleSave}>
                <div className="bg-white px-6 pt-5 pb-4">
                  <h3 className="text-lg font-medium text-gray-900 mb-1">
                    Modifica Limiti — {selectedCompany.ragione_sociale}
                  </h3>
                  <p className="text-sm text-gray-500 mb-5">
                    Lascia vuoti i limiti personalizzati per usare i valori di default del piano selezionato.
                  </p>

                  {error && (
                    <div className="mb-4 bg-red-100 border border-red-400 text-red-700 px-4 py-2 rounded text-sm">
                      {error}
                    </div>
                  )}

                  <div className="space-y-5">
                    {/* Piano abbonamento */}
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Piano Abbonamento
                      </label>
                      <select
                        value={editForm.subscription_plan}
                        onChange={e => setEditForm(prev => ({ ...prev, subscription_plan: e.target.value }))}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                      >
                        <option value="PRO">PRO — {formatNumber(PLAN_DEFAULTS.PRO.word_limit)} parole, {formatNumber(PLAN_DEFAULTS.PRO.query_limit)} query</option>
                        <option value="BUSINESS">BUSINESS — {formatNumber(PLAN_DEFAULTS.BUSINESS.word_limit)} parole, {formatNumber(PLAN_DEFAULTS.BUSINESS.query_limit)} query</option>
                        <option value="ENTERPRISE">ENTERPRISE — {formatNumber(PLAN_DEFAULTS.ENTERPRISE.word_limit)} parole, {formatNumber(PLAN_DEFAULTS.ENTERPRISE.query_limit)} query</option>
                      </select>
                      <p className="text-xs text-gray-500 mt-1">
                        Default piano selezionato: {formatNumber(PLAN_DEFAULTS[editForm.subscription_plan]?.word_limit)} parole / {formatNumber(PLAN_DEFAULTS[editForm.subscription_plan]?.query_limit)} query
                      </p>
                    </div>

                    {/* Limite parole personalizzato */}
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Limite Parole Personalizzato
                      </label>
                      <input
                        type="number"
                        min="1"
                        value={editForm.custom_word_limit}
                        onChange={e => setEditForm(prev => ({ ...prev, custom_word_limit: e.target.value }))}
                        placeholder={`Default: ${formatNumber(PLAN_DEFAULTS[editForm.subscription_plan]?.word_limit)}`}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                      />
                      <p className="text-xs text-gray-500 mt-1">Lascia vuoto per usare il default del piano</p>
                    </div>

                    {/* Limite query personalizzato */}
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Limite Query Personalizzato
                      </label>
                      <input
                        type="number"
                        min="1"
                        value={editForm.custom_query_limit}
                        onChange={e => setEditForm(prev => ({ ...prev, custom_query_limit: e.target.value }))}
                        placeholder={`Default: ${formatNumber(PLAN_DEFAULTS[editForm.subscription_plan]?.query_limit)}`}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                      />
                      <p className="text-xs text-gray-500 mt-1">Lascia vuoto per usare il default del piano</p>
                    </div>
                  </div>
                </div>

                <div className="bg-gray-50 px-6 py-3 sm:flex sm:flex-row-reverse gap-2">
                  <button
                    type="submit"
                    disabled={saving}
                    className="w-full inline-flex justify-center rounded-md border border-transparent shadow-sm px-4 py-2 bg-blue-600 text-base font-medium text-white hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 sm:w-auto sm:text-sm disabled:opacity-60"
                  >
                    {saving ? 'Salvataggio...' : 'Salva Modifiche'}
                  </button>
                  <button
                    type="button"
                    onClick={closeModal}
                    className="mt-3 w-full inline-flex justify-center rounded-md border border-gray-300 shadow-sm px-4 py-2 bg-white text-base font-medium text-gray-700 hover:bg-gray-50 focus:outline-none sm:mt-0 sm:w-auto sm:text-sm"
                  >
                    Annulla
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default SubscriptionManagement;
